import React, { useMemo } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import {
  formatCompanyBranchLabel,
  isLikelyStrapiDocumentId,
  parseStoredParameterUjiDocumentIds,
  type StrapiQuotationRow,
  type StrapiQuotationUserStub,
} from '../../api/strapiCrm';
import { ChemParameterUjiCell } from '../../components/ChemParameterUjiCell';
import { useParameterUjisByDocumentIdsQuery, useQuotationDetailQuery } from '../../query/hooks';
import { quotationStatusDisplayLabel, quotationStatusLabel } from './quotationWorkflow';
import { quotationMoneyTotalsFromRow, quotationPreviewSampleSectionsFromQuotationRow } from './quotationGrandTotal';
import { quotationFormSampleLineSubtotal } from './QuotationFormRoute/quotationFormSampleMoney';
import type { QuotationFormOtherCostRow } from './QuotationFormRoute/quotationFormTypes';

function otherCostLineSubtotal(row: QuotationFormOtherCostRow): number {
  const qty = Number.parseInt(row.quantity.trim(), 10);
  const price = Number.parseFloat(row.cost_price.replace(',', '.'));
  if (!Number.isFinite(qty) || qty < 1 || !Number.isFinite(price) || price < 0) return 0;
  return qty * price;
}

type Props = {
  open: boolean;
  documentId: string | null;
  jwt: string | null;
  onClose: () => void;
};

function formatDate(s?: string | null): string {
  if (!s) return '—';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' });
}

function formatUserStub(u?: StrapiQuotationUserStub | null): string {
  if (!u) return '—';
  const name = u.username?.trim();
  if (name) return name;
  return u.email?.trim() || '—';
}

function formatIdr(n: number): string {
  return n.toLocaleString('id-ID', { maximumFractionDigits: 0 });
}

function formatIdrSignedPlus(n: number): string {
  return `+ Rp ${formatIdr(n)}`;
}

function formatIdrSignedMinus(n: number): string {
  return `− Rp ${formatIdr(n)}`;
}

function collectParameterUjiDocIds(row: StrapiQuotationRow | undefined): string[] {
  const set = new Set<string>();
  for (const line of row?.sample_informations ?? []) {
    for (const t of parseStoredParameterUjiDocumentIds(line.parameter_uji)) {
      if (isLikelyStrapiDocumentId(t)) set.add(t.trim());
    }
  }
  return [...set];
}

function PreviewField({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: '200px 1fr' },
        gap: { xs: 0.25, sm: 1 },
        py: 0.75,
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
        {label}
      </Typography>
      <Typography variant="body2" component="div">
        {value}
      </Typography>
    </Box>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      variant="subtitle2"
      sx={{
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: 0.4,
        mt: 3,
        mb: 1.5,
        pb: 0.75,
        borderBottom: '2px solid',
        borderColor: 'text.primary',
      }}
    >
      {children}
    </Typography>
  );
}

function SamplePreviewTable({
  row,
  parameterUjisMap,
  sampleRecurringCounts,
}: {
  row: StrapiQuotationRow;
  parameterUjisMap: ReadonlyMap<string, { nama_parameter: string | null; simbol: string | null }>;
  sampleRecurringCounts: number[];
}) {
  const { usesGroups, sections } = useMemo(
    () => quotationPreviewSampleSectionsFromQuotationRow(row),
    [row],
  );

  let lineNum = 0;
  let flatRecurringIdx = 0;

  const bodyRows: React.ReactNode[] = [];

  for (const section of sections) {
    if (usesGroups && section.sectionLabel) {
      bodyRows.push(
        <TableRow key={`section-${section.sectionLabel}-${lineNum}`} sx={{ bgcolor: 'grey.50' }}>
          <TableCell colSpan={7}>
            <Typography variant="body2" sx={{ fontWeight: 700, textTransform: 'uppercase' }}>
              {section.sectionLabel}
            </Typography>
          </TableCell>
        </TableRow>,
      );
    }

    for (const sample of section.sampleRows) {
      const recurringCount = usesGroups
        ? section.recurringCount
        : (sampleRecurringCounts[flatRecurringIdx] ?? 1);
      if (!usesGroups) flatRecurringIdx += 1;
      lineNum += 1;
      const lineSub = quotationFormSampleLineSubtotal(sample, recurringCount);
      const hargaNum = Number.parseFloat(sample.harga.replace(',', '.'));
      bodyRows.push(
        <TableRow key={sample.key}>
          <TableCell>{lineNum}</TableCell>
          <TableCell>
            {sample.jenisPengukuranLabel?.trim() || sample.jenisPengukuranMasterId || '—'}
          </TableCell>
          <TableCell>
            <ChemParameterUjiCell raw={sample.parameterUji} puLookup={parameterUjisMap} />
          </TableCell>
          <TableCell>{sample.bakuMutuLabel?.trim() || sample.bakuMutuMasterId || '—'}</TableCell>
          <TableCell>{sample.jumlahTitik || '—'}</TableCell>
          <TableCell>
            {sample.harga.trim() && Number.isFinite(hargaNum) ? `Rp ${formatIdr(hargaNum)}` : '—'}
          </TableCell>
          <TableCell align="right">{lineSub > 0 ? `Rp ${formatIdr(lineSub)}` : '—'}</TableCell>
        </TableRow>,
      );
    }

    if (usesGroups) {
      for (const cost of section.otherCostRows) {
        lineNum += 1;
        const sub = otherCostLineSubtotal(cost);
        const priceNum = Number.parseFloat(cost.cost_price.replace(',', '.'));
        bodyRows.push(
          <TableRow key={cost.key} sx={{ bgcolor: 'grey.25' }}>
            <TableCell>{lineNum}</TableCell>
            <TableCell colSpan={3}>{cost.description.trim() || 'Biaya lain'}</TableCell>
            <TableCell>{cost.quantity}</TableCell>
            <TableCell>
              {cost.cost_price.trim() && Number.isFinite(priceNum)
                ? `Rp ${formatIdr(priceNum)}`
                : '—'}
            </TableCell>
            <TableCell align="right">{sub > 0 ? `Rp ${formatIdr(sub)}` : '—'}</TableCell>
          </TableRow>,
        );
      }
    }
  }

  return (
    <Table size="small" sx={{ border: 1, borderColor: 'divider' }}>
      <TableHead>
        <TableRow sx={{ bgcolor: 'grey.100' }}>
          <TableCell width={40}>#</TableCell>
          <TableCell>Jenis pengukuran</TableCell>
          <TableCell>Parameter uji</TableCell>
          <TableCell>Baku mutu</TableCell>
          <TableCell width={90}>Jumlah titik</TableCell>
          <TableCell width={110}>Harga</TableCell>
          <TableCell width={110} align="right">
            Subtotal
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {bodyRows.length === 0 ? (
          <TableRow>
            <TableCell colSpan={7}>
              <Typography variant="body2" color="text.secondary">
                Tidak ada sampel.
              </Typography>
            </TableCell>
          </TableRow>
        ) : (
          bodyRows
        )}
      </TableBody>
    </Table>
  );
}

function QuotationPreviewBody({
  row,
  parameterUjisMap,
}: {
  row: StrapiQuotationRow;
  parameterUjisMap: ReadonlyMap<string, { nama_parameter: string | null; simbol: string | null }>;
}) {
  const { financial, otherCostRows, totals } = useMemo(
    () => quotationMoneyTotalsFromRow(row),
    [row],
  );
  const sampleRecurringCounts = financial.recurringCounts;
  const groupSubtotals = financial.groupSubtotals;
  const groupOtherCostSubtotals = financial.groupOtherCostSubtotals;
  const usesGroups = financial.usesGroups;

  const remarkEdit = (row.remark_edit ?? '').trim();
  const remarkApproval = (row.remark_approval ?? '').trim();

  return (
    <Box
      sx={{
        position: 'relative',
        bgcolor: '#fff',
        color: 'text.primary',
        px: { xs: 2, sm: 4 },
        py: 3,
        minHeight: '100%',
        fontFamily: 'Arial, Helvetica, sans-serif',
      }}
      data-testid="quotation-preview-document"
    >
      <Box sx={{ position: 'relative', zIndex: 1 }}>
        <Typography
          variant="h4"
          align="center"
          sx={{ fontWeight: 700, letterSpacing: 1, fontSize: { xs: '1.35rem', sm: '1.75rem' } }}
        >
          QUOTATION
        </Typography>
        <Typography align="center" color="text.secondary" sx={{ mb: 3 }}>
          {row.quotation_number || '—'}
        </Typography>

        <SectionTitle>Informasi quotation</SectionTitle>
        <PreviewField label="Status" value={quotationStatusDisplayLabel(row)} />
        <PreviewField label="Pelanggan" value={row.customer?.name?.trim() || '—'} />
        <PreviewField label="No. pelanggan" value={row.customer?.customer_number?.trim() || '—'} />
        <PreviewField label="Cabang" value={formatCompanyBranchLabel(row.company_branch)} />
        <PreviewField label="Preorder" value={row.preorder ? 'Ya' : 'Tidak'} />
        <PreviewField label="Kontrak" value={row.is_contract ? 'Kontrak' : 'Non kontrak'} />
        <PreviewField
          label="Penawaran konsultan"
          value={row.is_consultant_quote ? 'Ya' : 'Tidak'}
        />
        <PreviewField
          label="Metode pengambilan sampel"
          value={row.sample_pickup_method === 'diantar' ? 'Diantar' : 'Sampling'}
        />
        <PreviewField label="Penanggung jawab" value={formatUserStub(row.assignee_user)} />
        <PreviewField label="Disetujui oleh" value={formatUserStub(row.approved_by_user)} />
        <PreviewField label="Tanggal dibuat" value={formatDate(row.createdAt)} />
        <PreviewField label="Tanggal permintaan" value={formatDate(row.request_date)} />

        <SectionTitle>Daftar sampel</SectionTitle>
        {usesGroups ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Pelanggan: <strong>{row.customer?.name?.trim() || '—'}</strong>
          </Typography>
        ) : null}
        <SamplePreviewTable
          row={row}
          parameterUjisMap={parameterUjisMap}
          sampleRecurringCounts={sampleRecurringCounts}
        />

        {!usesGroups ? (
          <>
            <SectionTitle>Biaya lainnya</SectionTitle>
            <Table size="small" sx={{ border: 1, borderColor: 'divider' }}>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell width={40}>#</TableCell>
                  <TableCell>Deskripsi</TableCell>
                  <TableCell width={80}>Qty</TableCell>
                  <TableCell width={110}>Harga satuan</TableCell>
                  <TableCell width={110} align="right">
                    Subtotal
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {otherCostRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Typography variant="body2" color="text.secondary">
                        Tidak ada biaya lainnya.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  otherCostRows.map((cost, idx) => {
                    const sub = totals.otherCostLineSubtotals[idx] ?? 0;
                    const priceNum = Number.parseFloat(cost.cost_price.replace(',', '.'));
                    return (
                      <TableRow key={cost.key}>
                        <TableCell>{idx + 1}</TableCell>
                        <TableCell>{cost.description.trim() || '—'}</TableCell>
                        <TableCell>{cost.quantity}</TableCell>
                        <TableCell>
                          {cost.cost_price.trim() && Number.isFinite(priceNum)
                            ? `Rp ${formatIdr(priceNum)}`
                            : '—'}
                        </TableCell>
                        <TableCell align="right">Rp {formatIdr(sub)}</TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </>
        ) : null}

        <SectionTitle>Ringkasan biaya</SectionTitle>
        <Box sx={{ maxWidth: 420, ml: 'auto' }}>
          <Stack spacing={0.75}>
            {groupSubtotals.length > 0
              ? groupSubtotals.map((g) => (
                  <Stack key={g.groupKey} direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      + Total sampel Sampel ({g.groupIndex + 1})
                      {g.recurringCount > 1 ? ` × ${g.recurringCount} jadwal` : ''}
                    </Typography>
                    <Typography variant="body2">{formatIdrSignedPlus(g.subtotal)}</Typography>
                  </Stack>
                ))
              : null}
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                + Total sampel
                {groupSubtotals.length > 1 ? ' (semua group)' : ''}
              </Typography>
              <Typography variant="body2">{formatIdrSignedPlus(totals.samplesSubtotal)}</Typography>
            </Stack>
            {groupOtherCostSubtotals.length > 0
              ? groupOtherCostSubtotals.map((g) => (
                  <Stack key={`other-${g.groupKey}`} direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      + Total biaya lainnya Sampel ({g.groupIndex + 1})
                      {g.recurringCount > 1 ? ` × ${g.recurringCount} jadwal` : ''}
                    </Typography>
                    <Typography variant="body2">{formatIdrSignedPlus(g.subtotal)}</Typography>
                  </Stack>
                ))
              : null}
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                + Total biaya lainnya
                {groupOtherCostSubtotals.length > 1 ? ' (semua group)' : ''}
              </Typography>
              <Typography variant="body2">
                {formatIdrSignedPlus(totals.otherCostsSubtotal)}
              </Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Jumlah sebelum diskon
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Rp {formatIdr(totals.subtotalBeforeDiscount)}
              </Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                − Diskon (nominal)
              </Typography>
              <Typography variant="body2" color={totals.discountAppliedRupiah > 0 ? 'error.main' : 'text.secondary'}>
                {formatIdrSignedMinus(totals.discountAppliedRupiah)}
              </Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Jumlah setelah diskon
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Rp {formatIdr(totals.afterDiscount)}
              </Typography>
            </Stack>
            {row.is_ppn ? (
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  + PPN 11%
                </Typography>
                <Typography variant="body2" color="success.main">
                  {formatIdrSignedPlus(totals.ppnAmount)}
                </Typography>
              </Stack>
            ) : null}
            <Divider sx={{ my: 0.5 }} />
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Grand total
              </Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Rp {formatIdr(totals.grandTotal)}
              </Typography>
            </Stack>
          </Stack>
        </Box>

        {remarkEdit || remarkApproval ? (
          <>
            <SectionTitle>Catatan</SectionTitle>
            {remarkEdit ? (
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                  Catatan revisi
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {remarkEdit}
                </Typography>
              </Box>
            ) : null}
            {remarkApproval ? (
              <Box>
                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                  Catatan persetujuan
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {remarkApproval}
                </Typography>
              </Box>
            ) : null}
          </>
        ) : null}
      </Box>
    </Box>
  );
}

/** Modal preview quotation lengkap (gaya dokumen, scroll vertikal tanpa tab). */
export function QuotationPreviewDialog({ open, documentId, jwt, onClose }: Props) {
  const detailQ = useQuotationDetailQuery(open ? jwt : null, open ? documentId : null);
  const parameterDocIds = useMemo(
    () => collectParameterUjiDocIds(detailQ.data),
    [detailQ.data],
  );
  const parameterUjisLookupQ = useParameterUjisByDocumentIdsQuery(jwt, parameterDocIds);
  const parameterUjisMap = parameterUjisLookupQ.data ?? new Map();

  const detailReady =
    detailQ.isSuccess &&
    detailQ.data &&
    (parameterDocIds.length === 0 ||
      parameterUjisLookupQ.isSuccess ||
      parameterUjisLookupQ.isError);

  const viewHref = documentId
    ? `/quotation/${encodeURIComponent(documentId)}/view`
    : '/quotations';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      scroll="paper"
      aria-labelledby="quotation-preview-dialog-title"
      data-testid="quotation-preview-dialog"
      PaperProps={{
        sx: {
          height: { xs: '100%', sm: '92vh' },
          maxHeight: { xs: '100%', sm: '92vh' },
          m: { xs: 0, sm: 2 },
          width: { xs: '100%', sm: 'calc(100% - 32px)' },
        },
      }}
    >
      <DialogTitle
        id="quotation-preview-dialog-title"
        sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, pr: 1, flexShrink: 0 }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" component="span" sx={{ fontWeight: 600 }}>
            Preview quotation
          </Typography>
          {detailQ.data?.quotation_number ? (
            <Typography variant="body2" color="text.secondary" display="block">
              {detailQ.data.quotation_number}
              {detailQ.data.progress_status ? ` · ${quotationStatusLabel(detailQ.data.progress_status)}` : ''}
            </Typography>
          ) : null}
        </Box>
        <IconButton aria-label="Tutup preview" onClick={onClose} edge="end" size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          p: 0,
          flex: 1,
          overflow: 'auto',
          bgcolor: '#e8e8e8',
        }}
      >
        {detailQ.isPending || (detailQ.isSuccess && !detailReady) ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={280}>
            <CircularProgress size={32} />
          </Box>
        ) : null}

        {detailQ.isError ? (
          <Box sx={{ p: 2 }}>
            <Alert severity="error">
              {detailQ.error instanceof Error ? detailQ.error.message : 'Gagal memuat quotation.'}
            </Alert>
          </Box>
        ) : null}

        {detailReady && detailQ.data ? (
          <QuotationPreviewBody row={detailQ.data} parameterUjisMap={parameterUjisMap} />
        ) : null}
      </DialogContent>

      <DialogActions sx={{ flexShrink: 0, px: 2, py: 1.5 }}>
        <Button onClick={onClose} color="inherit" data-testid="quotation-preview-close">
          Tutup
        </Button>
        {documentId ? (
          <Button
            component={RouterLink}
            to={viewHref}
            variant="outlined"
            startIcon={<OpenInNewOutlinedIcon />}
            onClick={onClose}
            data-testid="quotation-preview-open-detail"
          >
            Buka halaman detail
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
}
